/* eslint-disable no-return-assign */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { css, cx, keyframes } from 'emotion';
import Color from 'color';

import presets, { baselineGrid, colors, zIndex } from '../utils/presets';
import { options } from '../utils/typography';
import { fp, toRem, toVW, mqObj as mq } from '../utils/functions';
import gray from '../utils/gray';

import InnerWrapper from '../components/inner-wrapper';
import Button from '../components/button';
import TopShape from '../assets/svg-sprite/shape-newsletter-curved-top.svg';

const { speed, curve } = presets.animation;
const bp = '@media (min-width: 480px)';
const paddingTop = `${110 / 704 * 100}%`;

const formBackgroundColor = colors.accent.green;
const formBackgroundAltColor = Color(formBackgroundColor)
  .rotate(8)
  .lighten(0.24);
const formTextColor = gray(32, 'cool');
const formButtonColor = formTextColor;
const formAccentColor = colors.blue;
const formButtonColorHover = formAccentColor;
const wavesColor = Color(formBackgroundAltColor)
  .hex()
  .replace('#', '');
const wavesStrokeWidth = 12;
const formBackgroundGradient = `
  linear-gradient(to top right, transparent 40%, ${Color(formBackgroundColor)
    .rotate(16)
    .lighten(0)})
`;

const formMargin = fp({
  margin: [
    `${toRem(baselineGrid * 4)} auto`,
    `${toRem(baselineGrid * 6)} auto`,
    `${toRem(baselineGrid * 8)} auto`,
  ],
});

const cover = {
  position: 'absolute',
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
};

const subscribeFormWrapper = css`
  ${formMargin};
  max-width: ${toRem(baselineGrid * 144)};
`;

const subscribeForm = css`
  position: relative;
  margin: ${toRem(baselineGrid * -2)} auto 0;
  padding: ${toRem(baselineGrid * 3)};
  color: ${formTextColor};
  background-color: ${formBackgroundColor};
  border-radius: ${presets.radius.default};
  overflow: hidden;

  ${mq.lap} {
    padding: ${toRem(baselineGrid * 4)};
  }

  ${mq.lapLarge} {
    padding: ${toRem(baselineGrid * 6)};
  }

  ${mq.desk} {
    padding: ${toRem(baselineGrid * 8)};
  }
`;

const blobForm = css`
  margin: 0 auto;
  padding-bottom: 16% !important;
  border-bottom-left-radius: 50% 18%;
  border-bottom-right-radius: 50% 18%;

  ${mq.lap} {
    border-bottom-left-radius: 50% 24%;
    border-bottom-right-radius: 50% 24%;
  }
`;

const subscribeFormWavyTop = css`
  padding-top: calc(${paddingTop} + ${toRem(baselineGrid * 3)});
  background-color: ${formBackgroundColor};
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='250' height='30' viewBox='0 0 1000 120'%3E%3Cg fill='none' stroke='%23${wavesColor}' stroke-width='${wavesStrokeWidth}' %3E%3Cpath d='M-500 75c0 0 125-30 250-30S0 75 0 75s125 30 250 30s250-30 250-30s125-30 250-30s250 30 250 30s125 30 250 30s250-30 250-30'/%3E%3Cpath d='M-500 45c0 0 125-30 250-30S0 45 0 45s125 30 250 30s250-30 250-30s125-30 250-30s250 30 250 30s125 30 250 30s250-30 250-30'/%3E%3Cpath d='M-500 105c0 0 125-30 250-30S0 105 0 105s125 30 250 30s250-30 250-30s125-30 250-30s250 30 250 30s125 30 250 30s250-30 250-30'/%3E%3Cpath d='M-500 15c0 0 125-30 250-30S0 15 0 15s125 30 250 30s250-30 250-30s125-30 250-30s250 30 250 30s125 30 250 30s250-30 250-30'/%3E%3Cpath d='M-500-15c0 0 125-30 250-30S0-15 0-15s125 30 250 30s250-30 250-30s125-30 250-30s250 30 250 30s125 30 250 30s250-30 250-30'/%3E%3Cpath d='M-500 135c0 0 125-30 250-30S0 135 0 135s125 30 250 30s250-30 250-30s125-30 250-30s250 30 250 30s125 30 250 30s250-30 250-30'/%3E%3C/g%3E%3C/svg%3E"), ${formBackgroundGradient};

  ${mq.lap} {
    padding-top: calc(${paddingTop} + ${toRem(baselineGrid * 4)});
  }

  ${mq.lapLarge} {
    padding-top: calc(${paddingTop} + ${toRem(baselineGrid * 4)});
  }

  ${mq.desk} {
    padding-top: calc(${paddingTop} + ${toRem(baselineGrid * 4)});
  }
`;

const subscribeFormTop = css`
  position: absolute;
  left: 50%;
  top: 0;
  width: 100.44%;
  transform: translate(-50%, 0);
  padding-bottom: ${110 / 704 * 100}%;
  z-index: ${zIndex[2]};
`;

const subscribeFormTopShape = css`
  ${cover};
  display: block;
  fill: ${colors.ui.bright};
  pointer-events: none;
`;

const subscribeFormPlug = css`
  margin-bottom: ${toRem(baselineGrid * 2)};
  font-family: ${options.headerFontFamily.join(',')};
  font-weight: ${options.headerWeight};
  font-size: calc(${toRem(baselineGrid * 2)} + ${toVW(baselineGrid * 2)});
  line-height: 1.2;
`;

const subscribeFormStatus = css`
  display: inline-block;
  padding: ${toRem(baselineGrid * 0.75)} ${toRem(baselineGrid)};
  line-height: 1.2;
  color: ${colors.ui.bright};
  background-color: ${colors.gray.copy};
  border-radius: ${presets.radius.default};

  &.error {
    background-color: hsl(352, 100%, 44%);
  }

  a {
    color: inherit;
    font-weight: bolder;
  }
`;

const subscribeFormFields = css`
  display: flex;
  flex-wrap: wrap;
  position: relative;
  margin-top: ${toRem(baselineGrid * 3)};

  &::after {
    display: none;
  }

  ${bp} {
    flex-wrap: nowrap;
    padding: ${toRem(baselineGrid)} 0;
    border-bottom: 4px solid ${formTextColor};

    &::after {
      display: block;
      position: absolute;
      height: 0;
      left: 0;
      bottom: -4px;
      border-bottom: 4px solid ${formAccentColor};
      font-family: ${options.headerFontFamily.join(',')};
      font-size: calc(${toRem(baselineGrid * 2)} + ${toVW(baselineGrid * 2)});
      overflow: hidden;
    }
  }

  ${mq.lap} {
    margin-top: ${toRem(baselineGrid * 4)};
  }

  ${mq.lapLarge} {
    margin-top: ${toRem(baselineGrid * 6)};
  }
`;

const autofill = keyframes`
  to {
    background-color: transparent;
    color: ${formAccentColor};
  }
`;

const subscribeFormInput = css`
  flex: 1 0 100%;
  align-self: flex-end;
  min-width: ${toRem(200)};
  font-family: ${options.headerFontFamily.join(',')};
  font-weight: ${options.headerWeight};
  font-size: calc(${toRem(baselineGrid * 2)} + ${toVW(baselineGrid * 2)});
  background: none;
  border: none;
  padding: 0;
  height: ${toRem(baselineGrid * 5)};
  line-height: ${toRem(baselineGrid * 5)};
  color: ${formAccentColor};
  animation-name: ${autofill};
  animation-fill-mode: both;

  ${bp} {
    flex: 1;
  }

  &::placeholder {
    color: ${formTextColor};
    opacity: 1;
  }

  &:focus {
    outline: 0;

    &::placeholder {
      opacity: 0.7;
    }
  }

  &:-webkit-autofill {
    background-color: lime !important;
  }
`;

const subscribeFormButton = css`
  flex: 0;
  margin: ${toRem(baselineGrid * 2)} 0 0;
  align-self: flex-end;
  white-space: nowrap;
  transition: opacity ${speed.default} ${curve.default}, transform ${speed.default} ${curve.default},
    background ${speed.default} ${curve.default}, color ${speed.default} ${curve.default};

  ${bp} {
    margin: 0 0 0 ${toRem(baselineGrid)};
  }

  &:hover,
  &:focus,
  &:active {
    color: ${Color(formButtonColorHover).isDark() ? colors.ui.bright : colors.gray.dark};
    background-color: ${formButtonColorHover};
  }
`;

class SubscribeForm extends Component {
  constructor(props) {
    super(props);

    this.state = {
      name: '',
      email: '',
      changed: false,
    };

    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleChange = this.handleChange.bind(this);
  }

  handleSubmit(event) {
    event.preventDefault();
    if (this.state.email.indexOf('@') > -1)
      this.props.onSubmitted({
        FNAME: this.state.name,
        EMAIL: this.state.email,
      });
  }

  handleChange(event) {
    const { target } = event;
    const { value, name } = target;

    this.setState({
      [name]: value,
    });
  }

  render() {
    const { status, message, blob } = this.props;

    return (
      <InnerWrapper className={subscribeFormWrapper} narrow>
        <form
          onSubmit={this.handleSubmit}
          className={cx(subscribeForm, subscribeFormWavyTop, {
            'has-focus': this.state.hasFocus,
            [blobForm]: blob,
          })}
        >
          <div className={subscribeFormTop}>
            <svg className={subscribeFormTopShape} viewBox={TopShape.viewBox}>
              <use xlinkHref={`#${TopShape.id}`} />
            </svg>
          </div>
          <div className={subscribeFormPlug}>
            <span>
              Subscribe to our newsletter and be notified about new theme releases and updates.
            </span>
          </div>
          {status === 'sending' && <div className={subscribeFormStatus}>Sending...</div>}
          {status === 'error' && (
            <div
              className={cx(subscribeFormStatus, 'error')}
              dangerouslySetInnerHTML={{ __html: message }}
            />
          )}
          {status === 'success' && (
            <div
              className={cx(subscribeFormStatus, 'success')}
              dangerouslySetInnerHTML={{ __html: message }}
            />
          )}

          <div
            className={subscribeFormFields}
            css={css`
              &::after {
                content: '${this.state.email}';
              }
            `}
          >
            <input
              type="text"
              name="name"
              value={this.state.name}
              placeholder="Your name"
              className={css`
                display: none;
              `}
            />
            <input
              className={subscribeFormInput}
              type="email"
              name="email"
              value={this.state.email}
              placeholder="you@email.com"
              onChange={this.handleChange}
            />
            <Button
              type={'submit'}
              className={subscribeFormButton}
              backgroundColor={formButtonColor}
            >
              {'Sign Up'}
            </Button>
          </div>
        </form>
      </InnerWrapper>
    );
  }
}

SubscribeForm.propTypes = {
  status: PropTypes.string,
  message: PropTypes.string,
  onSubmitted: PropTypes.func.isRequired,
  blob: PropTypes.bool,
};

SubscribeForm.defaultProps = {
  blob: true,
};

export default SubscribeForm;



// WEBPACK FOOTER //
// ./src/components/subscribe-form.js